import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { currency, num } from "@/lib/format";
import { CategoryBadge } from "@/components/Badges";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Archive, RotateCcw, Warehouse, Boxes, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "raw", label: "Raw Material" },
  { value: "packaging", label: "Packaging" },
  { value: "finished", label: "Finished Product" },
  { value: "merchandise", label: "Merchandise" },
];
const UNITS = ["units", "kg", "g", "lbs", "oz", "cases", "each"];

const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || x).join(", ");
  return fb;
};

const ArchivedTag = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-stone-200 text-stone-600 border border-stone-300">Archived</span>
);

const ToolbarToggle = ({ showArchived, setShowArchived, onAdd, addLabel }) => (
  <div className="flex items-center justify-between gap-3 p-4 border-b border-stone-200 bg-stone-50/50">
    <button data-testid="toggle-archived" onClick={() => setShowArchived((v) => !v)}
      className="inline-flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-stone-900">
      {showArchived ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      {showArchived ? "Hide archived" : "Show archived"}
    </button>
    <Button data-testid="btn-add" onClick={onAdd} className="bg-stone-900 hover:bg-stone-800 active:scale-[0.98]"><Plus className="h-4 w-4 mr-1.5" />{addLabel}</Button>
  </div>
);

/* ---------------- Remove / Archive dialog ---------------- */
function RemoveDialog({ open, onClose, label, usage, onArchive, onDelete }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" />Remove {label}</DialogTitle></DialogHeader>
        {usage && (usage.can_delete ? (
          <p className="text-sm text-stone-600 py-1">This isn't referenced anywhere. You can permanently delete it. This cannot be undone.</p>
        ) : (
          <div className="py-1 space-y-2">
            <p className="text-sm text-stone-600">This is still in use, so it can't be deleted. Archive it instead — it will drop out of active lists and pickers but its history stays intact and can be restored.</p>
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs space-y-1" data-testid="usage-references">
              <div className="font-semibold text-stone-500 uppercase tracking-wide">Referenced by</div>
              {usage.references.map((r, i) => (
                <div key={i} className="flex items-center justify-between"><span className="text-stone-700 capitalize">{r.type}</span><span className="font-mono text-stone-500">{num(r.count)}</span></div>
              ))}
            </div>
          </div>
        ))}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {usage && usage.can_delete
            ? <Button data-testid="confirm-delete" onClick={onDelete} className="bg-rose-600 hover:bg-rose-700"><Trash2 className="h-4 w-4 mr-1.5" />Delete permanently</Button>
            : <Button data-testid="confirm-archive" onClick={onArchive} className="bg-stone-900 hover:bg-stone-800"><Archive className="h-4 w-4 mr-1.5" />Archive instead</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Facilities ---------------- */
function FacilitiesManager() {
  const [rows, setRows] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState(null); // {id?, name, type, description, manager, accent}
  const [remove, setRemove] = useState(null); // {id, name, usage}
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/facilities?include_archived=true").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const visible = rows.filter((f) => showArchived || !f.archived);
  const isEdit = form && form.id;

  const openAdd = () => setForm({ name: "", type: "", description: "", manager: "", accent: "#78716C" });
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/facilities/${form.id}`, form); toast.success("Facility updated"); }
      else { await api.post("/facilities", form); toast.success("Facility added"); }
      setForm(null); await load();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const askRemove = async (f) => {
    const { data } = await api.get(`/facilities/${f.id}/usage`);
    setRemove({ id: f.id, name: f.name, usage: data });
  };
  const doArchive = async () => { await api.post(`/facilities/${remove.id}/archive`); toast.success("Facility archived"); setRemove(null); load(); };
  const doDelete = async () => {
    try { await api.delete(`/facilities/${remove.id}`); toast.success("Facility deleted"); setRemove(null); load(); }
    catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };
  const restore = async (f) => { await api.post(`/facilities/${f.id}/restore`); toast.success("Facility restored"); load(); };

  return (
    <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-hidden">
      <ToolbarToggle showArchived={showArchived} setShowArchived={setShowArchived} onAdd={openAdd} addLabel="Add Facility" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
              <th className="px-4 py-3 text-left">Facility</th><th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Manager</th><th className="px-4 py-3 text-right">Asset Value</th>
              <th className="px-4 py-3 text-right">SKUs</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((f) => (
              <tr key={f.id} data-testid={`manage-facility-${f.id}`} className={`border-b border-stone-100 last:border-0 ${f.archived ? "bg-stone-50/60 opacity-70" : "hover:bg-stone-50/70"}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full shrink-0" style={{ background: f.accent }} />
                    <span className="font-medium text-stone-800">{f.name}</span>
                    {f.archived && <ArchivedTag />}
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-600">{f.type}</td>
                <td className="px-4 py-3 text-stone-600">{f.manager}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-700">{currency(f.total_value)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-600">{num(f.item_count)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {f.archived ? (
                      <Button size="sm" variant="outline" data-testid={`restore-facility-${f.id}`} onClick={() => restore(f)} className="h-8"><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" data-testid={`edit-facility-${f.id}`} onClick={() => setForm({ ...f })} className="h-8 px-2 text-stone-600"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" data-testid={`remove-facility-${f.id}`} onClick={() => askRemove(f)} className="h-8 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && !saving && setForm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Warehouse className="h-5 w-5 text-amber-700" />{isEdit ? "Edit Facility" : "Add Facility"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3 py-1">
              <div><Label className="text-xs text-stone-500">Name</Label><Input data-testid="facility-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-stone-500">Type</Label><Input data-testid="facility-type-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Kitchen, Distribution…" className="mt-1" /></div>
                <div><Label className="text-xs text-stone-500">Manager</Label><Input data-testid="facility-manager-input" value={form.manager} onChange={(e) => setForm({ ...form, manager: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs text-stone-500">Description</Label><Textarea data-testid="facility-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1" /></div>
              <div className="flex items-center gap-3">
                <Label className="text-xs text-stone-500">Accent color</Label>
                <input data-testid="facility-accent-input" type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="h-9 w-14 rounded border border-stone-200 cursor-pointer" />
                <span className="font-mono text-xs text-stone-500">{form.accent}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} disabled={saving}>Cancel</Button>
            <Button data-testid="facility-save-btn" onClick={save} disabled={saving} className="bg-stone-900 hover:bg-stone-800">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RemoveDialog open={!!remove} onClose={() => setRemove(null)} label="facility" usage={remove?.usage} onArchive={doArchive} onDelete={doDelete} />
    </div>
  );
}

/* ---------------- SKUs ---------------- */
function SkusManager() {
  const [rows, setRows] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState(null);
  const [codeLocked, setCodeLocked] = useState(false);
  const [remove, setRemove] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/skus?include_archived=true").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/facilities").then((r) => setFacilities(r.data)); }, []);

  const visible = rows.filter((s) => showArchived || !s.archived);
  const isEdit = form && form._edit;

  const openAdd = () => { setCodeLocked(false); setForm({ sku_code: "", name: "", category: "raw", unit: "units", unit_cost: "", reorder_point: "", vendor: "", notes: "", stock: {} }); };
  const openEdit = async (s) => {
    setForm({ _edit: true, _orig: s.sku_code, sku_code: s.sku_code, name: s.name, category: s.category, unit: s.unit, unit_cost: s.unit_cost, reorder_point: s.reorder_point, vendor: s.vendor || "", notes: s.notes || "", stock: {} });
    const { data } = await api.get(`/skus/${s.sku_code}/usage`);
    setCodeLocked(data.code_locked);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, category: form.category, unit: form.unit, unit_cost: Number(form.unit_cost) || 0, reorder_point: Number(form.reorder_point) || 0, vendor: form.vendor, notes: form.notes };
        if (!codeLocked && form.sku_code) payload.sku_code = form.sku_code;
        await api.put(`/skus/${form._orig || form.sku_code}`, payload);
        toast.success("SKU updated");
      } else {
        if (!form.sku_code.trim()) { toast.error("SKU code is required"); setSaving(false); return; }
        const stock = Object.fromEntries(Object.entries(form.stock).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
        await api.post("/skus", { sku_code: form.sku_code, name: form.name, category: form.category, unit: form.unit, unit_cost: Number(form.unit_cost) || 0, reorder_point: Number(form.reorder_point) || 0, vendor: form.vendor, notes: form.notes, stock });
        toast.success("SKU added");
      }
      setForm(null); await load();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const askRemove = async (s) => {
    const { data } = await api.get(`/skus/${s.sku_code}/usage`);
    setRemove({ code: s.sku_code, name: s.name, usage: data });
  };
  const doArchive = async () => { await api.post(`/skus/${remove.code}/archive`); toast.success("SKU archived"); setRemove(null); load(); };
  const doDelete = async () => {
    try { await api.delete(`/skus/${remove.code}`); toast.success("SKU deleted"); setRemove(null); load(); }
    catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };
  const restore = async (s) => { await api.post(`/skus/${s.sku_code}/restore`); toast.success("SKU restored"); load(); };

  return (
    <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-hidden">
      <ToolbarToggle showArchived={showArchived} setShowArchived={setShowArchived} onAdd={openAdd} addLabel="Add SKU" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
              <th className="px-4 py-3 text-left">SKU / Item</th><th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-right">On Hand</th><th className="px-4 py-3 text-right">Reorder</th>
              <th className="px-4 py-3 text-right">Unit Cost</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr key={s.sku_code} data-testid={`manage-sku-${s.sku_code}`} className={`border-b border-stone-100 last:border-0 ${s.archived ? "bg-stone-50/60 opacity-70" : "hover:bg-stone-50/70"}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-medium text-stone-800">{s.name}{s.archived && <ArchivedTag />}</div>
                  <div className="text-xs font-mono text-stone-500 mt-0.5">{s.sku_code} · {s.vendor}</div>
                </td>
                <td className="px-4 py-3"><CategoryBadge category={s.category} /></td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-700">{num(s.total_on_hand)} {s.unit}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-500">{num(s.reorder_point)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-600">{currency(s.unit_cost)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {s.archived ? (
                      <Button size="sm" variant="outline" data-testid={`restore-sku-${s.sku_code}`} onClick={() => restore(s)} className="h-8"><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" data-testid={`edit-sku-${s.sku_code}`} onClick={() => openEdit(s)} className="h-8 px-2 text-stone-600"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" data-testid={`remove-sku-${s.sku_code}`} onClick={() => askRemove(s)} className="h-8 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && !saving && setForm(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Boxes className="h-5 w-5 text-amber-700" />{isEdit ? "Edit SKU" : "Add SKU"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-500">SKU Code {isEdit && codeLocked && <span className="text-stone-400">(locked)</span>}</Label>
                  <Input data-testid="sku-code-input" value={form.sku_code} disabled={isEdit && codeLocked} onChange={(e) => setForm({ ...form, sku_code: e.target.value.toUpperCase() })} placeholder="RAW-XXX" className="mt-1 font-mono" />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="sku-category-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs text-stone-500">Name</Label><Input data-testid="sku-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-stone-500">Unit</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="sku-unit-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-stone-500">Unit Cost</Label><Input data-testid="sku-cost-input" type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs text-stone-500">Reorder Pt</Label><Input data-testid="sku-reorder-input" type="number" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs text-stone-500">Vendor</Label><Input data-testid="sku-vendor-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs text-stone-500">Notes</Label><Input data-testid="sku-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
              {!isEdit && (
                <div className="border-t border-stone-100 pt-3">
                  <Label className="text-xs text-stone-500">Starting on-hand stock (optional)</Label>
                  <div className="space-y-2 mt-2">
                    {facilities.map((f) => (
                      <div key={f.id} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-stone-600">{f.name}</span>
                        <Input data-testid={`sku-stock-${f.id}`} type="number" value={form.stock[f.id] ?? ""} onChange={(e) => setForm({ ...form, stock: { ...form.stock, [f.id]: e.target.value } })} placeholder="0" className="w-28 h-8" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} disabled={saving}>Cancel</Button>
            <Button data-testid="sku-save-btn" onClick={save} disabled={saving} className="bg-stone-900 hover:bg-stone-800">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RemoveDialog open={!!remove} onClose={() => setRemove(null)} label="SKU" usage={remove?.usage} onArchive={doArchive} onDelete={doDelete} />
    </div>
  );
}

export default function Manage() {
  const [tab, setTab] = useState("facilities");
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader title="Manage" subtitle="Master data — add, edit, and archive the facilities & SKUs that power the app" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100">
          <TabsTrigger value="facilities" data-testid="manage-tab-facilities"><Warehouse className="h-4 w-4 mr-1.5" />Facilities</TabsTrigger>
          <TabsTrigger value="skus" data-testid="manage-tab-skus"><Boxes className="h-4 w-4 mr-1.5" />SKUs</TabsTrigger>
        </TabsList>
        <TabsContent value="facilities"><FacilitiesManager /></TabsContent>
        <TabsContent value="skus"><SkusManager /></TabsContent>
      </Tabs>
    </div>
  );
}
